/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, February 16th 2022, 8:30:00 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { FormField, FIELD_TYPES } from "lib/schemas";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Select from "@mui/material/Select";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import { COLORSCHEME } from "theme/styles";
import Paper from "@mui/material/Paper";
import { getFieldTypeWidgets } from "./FieldTypeWidgets";

type GenericFieldPreviewProps = Pick<FormField, "label" | "field_type">;

export const FieldPreview = ({ label, field_type }: GenericFieldPreviewProps) => {
  switch (field_type) {
    case FIELD_TYPES.TEXT:
      return <TextFieldPreview label={label} />;

    case FIELD_TYPES.TEXTAREA:
      return <TextAreaFieldPreview label={label} />;

    case FIELD_TYPES.EMAIL:
      return <EmailFieldPreview label={label} />;

    case FIELD_TYPES.CHECKBOX:
      return <CheckBoxFieldPreview label={label} />;

    case FIELD_TYPES.CHECKBOX_MULTIPLE:
      return <CheckBoxMultipleFieldPreview label={label} />;

    case FIELD_TYPES.SELECT:
      return <SelectFieldPreview label={label} />;

    case FIELD_TYPES.SELECT_MULTIPLE:
      return <SelectMultipleFieldPreview label={label} />;

    case FIELD_TYPES.RADIO_MULTIPLE:
      return <RadioMultipleFieldPreview label={label} />;

    case FIELD_TYPES.FILE:
      return <FileFieldPreview label={label} />;

    case FIELD_TYPES.DATE:
      return <DateFieldPreview label={label} />;

    case FIELD_TYPES.DATE_TIME:
      return <DateTimeFieldPreview label={label} />;

    case FIELD_TYPES.HIDDEN:
      return <HiddenFieldPreview label={label} />;

    case FIELD_TYPES.NUMBER:
      return <NumberFieldPreview label={label} />;

    case FIELD_TYPES.URL:
      return <URLFieldPreview label={label} />;

    case FIELD_TYPES.DOB:
      return <DOBFieldPreview label={label} />;

    case FIELD_TYPES.SECTION_BREAK:
      return <SectionBreakFieldPreview label={label} />;

    case FIELD_TYPES.GEO_COORDINATES:
      return <GISCoordinatesFieldPreview label={label} />;

    case FIELD_TYPES.MULTIPLE_FILE:
      return <MultipleFileFieldPreview label={label} />;

    case FIELD_TYPES.PAGE_TITLE:
      return <PageTitleFieldPreview label={label} />;

    case FIELD_TYPES.BUTTON_CHECK:
      return <ButtonCheckFieldPreview label={label} />;

    case FIELD_TYPES.MATRIX:
      return <MatrixFieldPreview label={label} />;

    case FIELD_TYPES.FORM_ENTRIES:
      return <FormEntriesFieldPreview label={label} />;

    case FIELD_TYPES.TITLE:
      return <TitleFieldPreview label={label} />;

    case FIELD_TYPES.MULTI_FORM_ENTRIES:
      return <MultiFormEntriesFieldPreview label={label} />;

    case FIELD_TYPES.AUTO_COMPLETE:
      return <AutoCompleteFieldPreview label={label} />;

    case FIELD_TYPES.SOURCE_COMPLETE:
      return <SourceCompleteFieldPreview label={label} />;

    case FIELD_TYPES.MATRIX_VIEW:
      return <MatrixViewFieldPreview label={label} />;

    case FIELD_TYPES.SIGNATURE_PAD:
      return <SignaturePadFieldPreview label={label} />;

    case FIELD_TYPES.IMAGE:
      return <ImageFieldPreview label={label} />;

    case FIELD_TYPES.PHONE_NUMBER:
      return <PhoneNumberFieldPreview label={label} />;

    case FIELD_TYPES.DYNAMIC_MATRIX:
      return <DynamicMatrixFieldPreview label={label} />;

    case FIELD_TYPES.AUTO_GENERATED_SEQUENCE:
      return <AutoSequenceFieldPreview label={label} />;

    default:
      return <DefaultPreview label={label} />;
  }
};

type ConcreteFieldPreviewProps = Pick<FormField, "label">;

const TextFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <TextField
    className="os-form-builder-field-preview-MUIInput"
    label={label}
    value={label}
    InputProps={{
      readOnly: true,
      startAdornment: getFieldTypeWidgets(FIELD_TYPES.TEXT)[1],
    }}
    fullWidth
  />
);

const TextAreaFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <TextField
    className="os-form-builder-field-preview-MUIInput"
    label={label}
    value={label}
    multiline
    rows={4}
    InputProps={{
      readOnly: true,
      startAdornment: getFieldTypeWidgets(FIELD_TYPES.TEXTAREA)[1],
    }}
    fullWidth
  />
);

const EmailFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <TextField
    className="os-form-builder-field-preview-MUIInput"
    label={label}
    value={label}
    InputProps={{
      readOnly: true,
      startAdornment: getFieldTypeWidgets(FIELD_TYPES.EMAIL)[1],
    }}
    fullWidth
  />
);

const CheckBoxFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <FormControl className="os-form-builder-field-preview-MUIInput">
    <FormLabel>{label}</FormLabel>
    <Checkbox defaultChecked />
  </FormControl>
);

const CheckBoxMultipleFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <FormGroup className="os-form-builder-field-preview-MUIInput">
    <FormLabel>{label}</FormLabel>
    <FormControlLabel control={<Checkbox defaultChecked />} label={label} />
    <FormControlLabel disabled control={<Checkbox />} label={label} />
    <FormControlLabel disabled control={<Checkbox />} label={label} />
  </FormGroup>
);

const SelectFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <FormControl className="os-form-builder-field-preview-MUIInput" fullWidth>
    <InputLabel>{label}</InputLabel>
    <Select value={label} label={label} />
  </FormControl>
);

const SelectMultipleFieldPreview = ({ label }: ConcreteFieldPreviewProps) => <SelectFieldPreview label={label} />;

const RadioMultipleFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <FormControl className="os-form-builder-field-preview-MUIInput">
    <FormLabel>{label}</FormLabel>
    <RadioGroup>
      <FormControlLabel value={`demo`} control={<Radio />} label="Option 1" />
      <FormControlLabel value={`demo-1`} control={<Radio />} label={`Option 2`} />
      <FormControlLabel value={`$demo-2`} control={<Radio />} label={`Option 3`} />
    </RadioGroup>
  </FormControl>
);

const FileFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <Button className="os-form-builder-field-preview-MUIInput" variant="contained" component="span">
    {getFieldTypeWidgets(FIELD_TYPES.FILE)[1]}
    {label}
  </Button>
);

const DateFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <TextField
    className="os-form-builder-field-preview-MUIInput"
    label={label}
    value={label}
    InputProps={{
      readOnly: true,
      startAdornment: getFieldTypeWidgets(FIELD_TYPES.DATE)[1],
    }}
    fullWidth
  />
);

const DateTimeFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <TextField
    className="os-form-builder-field-preview-MUIInput"
    label={label}
    value={label}
    InputProps={{
      readOnly: true,
      startAdornment: getFieldTypeWidgets(FIELD_TYPES.DATE_TIME)[1],
    }}
    fullWidth
  />
);

const HiddenFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <>
    {getFieldTypeWidgets(FIELD_TYPES.HIDDEN)[1]} {label}
  </>
);

const NumberFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <TextField
    className="os-form-builder-field-preview-MUIInput"
    label={label}
    value={label}
    InputProps={{
      readOnly: true,
      startAdornment: getFieldTypeWidgets(FIELD_TYPES.NUMBER)[1],
    }}
    fullWidth
  />
);

const URLFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <TextField
    className="os-form-builder-field-preview-MUIInput"
    label={label}
    value={label}
    InputProps={{
      readOnly: true,
      startAdornment: getFieldTypeWidgets(FIELD_TYPES.URL)[1],
    }}
    fullWidth
  />
);

const DOBFieldPreview = ({ label }: ConcreteFieldPreviewProps) => <DateFieldPreview label={label} />;

const SectionBreakFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <Typography color={COLORSCHEME.primary}>
    <Stack direction="column" spacing={2}>
      <div>{getFieldTypeWidgets(FIELD_TYPES.SECTION_BREAK)[1]} SECTION</div>
      <div>{label}</div>
    </Stack>
  </Typography>
);

const GISCoordinatesFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <TextField
    className="os-form-builder-field-preview-MUIInput"
    label={label}
    value={label}
    InputProps={{
      readOnly: true,
      startAdornment: getFieldTypeWidgets(FIELD_TYPES.GEO_COORDINATES)[1],
    }}
    fullWidth
  />
);

const MultipleFileFieldPreview = ({ label }: ConcreteFieldPreviewProps) => <FileFieldPreview label={label} />;

const PageTitleFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <Typography color={COLORSCHEME.primary}>
    <Stack direction="column" spacing={2}>
      <div>{getFieldTypeWidgets(FIELD_TYPES.PAGE_TITLE)[1]} PAGE</div>
      <div>{label}</div>
    </Stack>
  </Typography>
);

const ButtonCheckFieldPreview = ({ label }: ConcreteFieldPreviewProps) => <Button>{label}</Button>;

const MatrixFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <Grid container spacing={2}>
    <Grid item md={4}>
      <TextFieldPreview label={label} />
    </Grid>
    <Grid item md={4}>
      <TextFieldPreview label={label} />
    </Grid>
    <Grid item md={4}>
      <TextFieldPreview label={label} />
    </Grid>
    <Grid item md={4}>
      <TextFieldPreview label={label} />
    </Grid>
    <Grid item md={4}>
      <TextFieldPreview label={label} />
    </Grid>
    <Grid item md={4}>
      <TextFieldPreview label={label} />
    </Grid>
  </Grid>
);

const FormEntriesFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <Typography color={COLORSCHEME.primary}>
    <Stack direction="row" spacing={1}>
      <div>{getFieldTypeWidgets(FIELD_TYPES.FORM_ENTRIES)[1]}</div>
      <SelectFieldPreview label={label} />
    </Stack>
  </Typography>
);

const MultiFormEntriesFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <FormEntriesFieldPreview label={label} />
);

const TitleFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <Typography color={COLORSCHEME.primary}>
    <Stack direction="column" spacing={2}>
      <div>{getFieldTypeWidgets(FIELD_TYPES.TITLE)[1]} TITLE TEXT</div>
      <div>{label}</div>
    </Stack>
  </Typography>
);

const AutoCompleteFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <>
    <Typography color={COLORSCHEME.primary}>AUTO COMPLETE FIELD: {label}</Typography>:{" "}
    <SelectFieldPreview label={label} />
  </>
);

const SourceCompleteFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <>
    <Typography color={COLORSCHEME.primary}>SOURCE COMPLETE FIELD: {label}</Typography>:{" "}
    <SelectFieldPreview label={label} />
  </>
);

const MatrixViewFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <>
    <Typography color={COLORSCHEME.primary}>MATRIX VIEW {label}</Typography>
    <MatrixFieldPreview label={label} />
  </>
);

const SignaturePadFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <Paper sx={{ padding: "20px" }}>
    <Typography color={COLORSCHEME.primary}>
      <Stack direction="column" spacing={2}>
        <div style={{ color: COLORSCHEME.secondary }}>
          {getFieldTypeWidgets(FIELD_TYPES.SIGNATURE_PAD)[1]} SIGNATURE PAD
        </div>
        <div>{label}</div>
      </Stack>
    </Typography>
  </Paper>
);

const ImageFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <Stack direction="row">
    <div style={{ color: COLORSCHEME.primary }}>{getFieldTypeWidgets(FIELD_TYPES.IMAGE)[1]}</div>
    <Typography color={COLORSCHEME.primary}>{label}</Typography>
  </Stack>
);

const PhoneNumberFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <TextField
    className="os-form-builder-field-preview-MUIInput"
    label={label}
    value={label}
    InputProps={{
      readOnly: true,
      startAdornment: getFieldTypeWidgets(FIELD_TYPES.PHONE_NUMBER)[1],
    }}
    fullWidth
  />
);

const DynamicMatrixFieldPreview = ({ label }: ConcreteFieldPreviewProps) => <MatrixFieldPreview label={label} />;

const AutoSequenceFieldPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <Stack direction="row">
    <div style={{ color: COLORSCHEME.primary }}>{getFieldTypeWidgets(FIELD_TYPES.AUTO_GENERATED_SEQUENCE)[1]}</div>
    <Typography color={COLORSCHEME.primary}>{label}</Typography>
  </Stack>
);

const DefaultPreview = ({ label }: ConcreteFieldPreviewProps) => (
  <TextField className="os-form-builder-field-preview-MUIInput" label={label} value={label} fullWidth />
);
